<template>
  <div class="fts-query-designer">
    <template v-for="filter in filter.items">
      <div class="query-designer-container">
        <fts-label-group :item="getFilterInfo(filter)" :information-modules="modules" information emoji
          :emoji-size="14"></fts-label-group>
        <span>{{ getSearchTypeDisplay(filter) }}</span>
        <template v-if="getPayloadDisplay(filter)">
          <template v-if="getInformationType(filter.fieldId) === 'state'">
            <fts-state :state="filter.payload" :states="getStates" />
          </template>
          <template v-else-if="getInformationType(filter.fieldId) === 'Select' && filter.operator === 'is'">
            <div class="query-select-option" :style="{
              'background-color':
                getSelectOptionColor(filter),
            }" v-if="filter.payload">
              {{ filter.payload }}
            </div>
            <div class="query-select-option" v-else>any</div>
          </template>
          <span v-else>“{{ getPayloadDisplay(filter) }}”</span>
        </template>
        <font-awesome-icon class="remove-icon" :icon="['fas', 'times']" fixed-width @click="removeFilter(filter)" />
      </div>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import draggable from "vuedraggable";
import moment from "moment";

import commons from "../commons/commons";
import queryLogic from '../commons/queryLogic';
import FTSQuery from "../elements/FTSQuery";
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSState from "../elements/FTSState";
import FTSBtn from '../elements/FTSBtn';


export default {
  props: {
    modules: Array,
    states: Array,
    informationSource: Array,
    variables: Array,
    filter: Object,
    mode: String,
    allowAnyState: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    "fts-label-group": FTSLabelGroup,
    "fts-state": FTSState,
    "fts-btn": FTSBtn,
    draggable,
  },
  data() {
    return {
      dragOptions: commons.dragOptions
    };
  },
  methods: {
    getFilterInfo(filter) {
      const res = this.informationSource.find((x) => x.id == filter.fieldId);
      if (res) {
        return {
          "type": res.type,
          "displayName": res.label || res.displayName,
          "system": res.system,
          "connectionId": res.connectionId
        }
      }
    },
    getInformationType(fieldId) {
      return queryLogic.getInformationType(this.informationSource, fieldId);
    },
    getSearchTypeDisplay(filter) {
      // if (filter.type == "formula") {
      //   let operator = queryLogic
      //     .getSearchTypes("formula")
      //     .find((x) => x.fieldName == filter.operator);
      //   if (operator) return operator.displayName;
      // }

      let type = this.getInformationType(filter.fieldId);
      if (type) {
        let operator = queryLogic
          .getSearchTypes(type)
          .find((x) => x.fieldName == filter.operator);
        if (operator) return operator.displayName;
      }

      return "(unknown operator)";
    },
    getPayloadDisplay(filter) {
      const type = this.getInformationType(filter.fieldId);

      if (filter.operator === 'isNone' || filter.operator === 'none' || filter.operator === 'isNotNone' || filter.operator === 'me') {
        return null;
      }

      /* An unfilled unit is stored with no payload at all, or with payload: null (empty Date
         filters have been persisted that way since 05e56e34 - SS06087 / SS06088 / SS06089). The
         numeric branch below reads payload.min / payload.max directly, so a saved-but-empty unit
         threw while rendering its chip; the other branches rendered the literal string "null".
         There is nothing to show for a unit with no value.  */
      if (filter.payload === null || filter.payload === undefined) {
        return null;
      }

      if (type === 'Integer' || type === 'Number' || type === 'Money') {
        if (filter.operator === 'between') return `${filter.payload.min} to ${filter.payload.max}`;
        if (filter.operator === 'gte' || filter.operator === 'gt') return `${filter.payload.min}`;
        if (filter.operator === 'lte' || filter.operator === 'lt') return `${filter.payload.max}`;
        if (filter.operator === 'eq' || filter.operator === 'ne') return `${filter.payload}`;
        return null;
      }

      if (type === 'Date' || type === 'Date Time') {
        if (!filter.payload) return '';
        if (filter.operator === 'between') {
          const startDate = filter.payload.startDate;
          const endDate = filter.payload.endDate;
          return `${moment(startDate).format('MMM D, YYYY')} - ${moment(endDate).format('MMM D, YYYY')}`
        }
        if (filter.operator === 'within') {
          const options = queryLogic.getDatePredefinedselection(filter.payload.duration);
          return options.find((x) => x.offset === filter.payload.offset).displayName;
        }
        if (filter.operator === 'gt' || filter.operator === 'lt' || filter.operator === 'gte' || filter.operator === 'lte' || filter.operator === 'is') {
          return `${moment(filter.payload).format('MMM D, YYYY')}`
        }
        if (filter.operator === 'in' || filter.operator === 'past') {
          return `${filter.payload.offset} ${filter.payload.duration}`;
        }
      }

      if (type === 'Select') {
        return `${filter.payload}`;
      }

      if (!filter.payload) return null;
      return `${filter.payload}`;
    },
    getSelectOptionColor(item) {
      return commons.selectOptionBackground({
        optionList:
          this.getSelectOptions(item) &&
          this.getSelectOptions(item).filter((x) => x.id !== null),
        text: item.payload,
      });
    },
    getSelectOptions(item) {
      const information = this.informationSource.find(
        (x) => x.id === item.fieldId
      );
      if (
        information &&
        (information.type == 27 || information.type == 28) &&
        Array.isArray(information.optionList) &&
        information.optionList.length
      )
        return [{ id: null }].concat(information.optionList);
    },
    removeFilter(filter) {
      let ind = this.filter.items.indexOf(filter);
      this.filter.items.splice(ind, 1);
    },
    addFilter() {
      if (Array.isArray(this.informationSource) && this.informationSource.length) {
        this.filter.items.push({
          id: commons.nanoID(),
          ...queryLogic.prepareQueryUnit(
            this.informationSource,
            this.informationSource[0].id,
          ),
        });
      }
    },
  },
  computed: {
    getStates() {
      return this.allowAnyState ?
        [{ id: null, name: 'any' }].concat(this.states) :
        this.states;
    },
  },
  beforeCreate: function () {
    Vue.component("fts-query", FTSQuery);
  }
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars.scss" as *;

.fts-query-designer {
  display: flex;
  gap: 8px;
  row-gap: 8px;
  flex-wrap: wrap;
}

.query-designer-container {
  display: flex;
  background-color: var(--inistate-color-neutral-100);
  padding: 8px 12px;
  gap: 4px;
  border-radius: 4px;
  align-items: center;
  font-weight: 400;
  color: var(--inistate-text-secondary-color);
  flex: unset !important;

  .remove-icon {
    color: var(--inistate-text-secondary-color);
    cursor: pointer;
  }
}

.query-select-option {
  @include text-overflow-ellipsis;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  border-radius: 0.25rem;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  background-color: #d9d9d9;
  color: #000;
}
</style>
